import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Notification, NotificationFilter, NotificationType } from '../../../models/notification.model';
import { NotificationService } from '../../../services/notification.service';
import { Router } from '@angular/router';
import { NotificationModalSettingsComponent } from './notification-modal-settings/notification-modal-settings.component';

@Component({
  selector: 'app-notification-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatCheckboxModule,
    NotificationModalSettingsComponent
  ],
  templateUrl: './notification-modal.component.html',
  styleUrls: ['./notification-modal.component.scss']
})
export class NotificationModalComponent implements OnInit {
  notifications$!: Observable<Notification[]>;
  showSettings = false;
  selectedTabIndex = 0;
  
  private readonly TAB_TYPES = ['all', 'mentions', 'assigned', 'status_changes'] as const;
  filterSubject = new BehaviorSubject<NotificationFilter>({ type: 'all', showOnlyUnread: true });

  constructor(
    private dialogRef: MatDialogRef<NotificationModalComponent>,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.notifications$ = combineLatest([
      this.notificationService.getNotifications(),
      this.filterSubject
    ]).pipe(
      map(([notifications, filter]) => this.applyFilters(notifications, filter))
    );
  }

  applyFilters(notifications: Notification[], filter: NotificationFilter): Notification[] {
    let filtered = notifications;

    if (filter.showOnlyUnread) {
      filtered = filtered.filter(n => !n.isRead);
    }

    if (filter.type !== 'all') {
      const typeMap = {
        'mentions': NotificationType.MENTION,
        'assigned': NotificationType.ASSIGNED,
        'status_changes': NotificationType.STATUS_CHANGE
      };
      filtered = filtered.filter(n => n.type === typeMap[filter.type as keyof typeof typeMap]);
    }

    return filtered.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  onFilterChange(filter: NotificationFilter): void {
    this.filterSubject.next(filter);
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead();
  }

  onNotificationClick(notification: Notification): void {
    this.notificationService.markAsRead(notification.id);
    
    if (notification.entityType === 'intervention' && notification.interventionId) {
      this.router.navigate(['/intervention/coaching'], {
        queryParams: {
          interventionId: notification.interventionId,
          driverGuid: notification.driverGuid,
          assignee: notification.assignee || 'Unassigned',
          idDriver: notification.driverId,
          tab: 'CoachingDetails'
        }
      });
    }
    
    this.dialogRef.close();
  }

  showSettingsView(): void {
    this.showSettings = true;
  }

  showNotificationsView(): void {
    this.showSettings = false;
  }

  getNotificationIcon(type: NotificationType): string {
    switch (type) {
      case NotificationType.MENTION: return 'fa-at';
      case NotificationType.ASSIGNED: return 'fa-circle-user';
      case NotificationType.STATUS_CHANGE: return 'fa-right-left';
      default: return 'fa-bell';
    }
  }

  formatNotificationText(notification: Notification): string {
    switch (notification.type) {
      case NotificationType.MENTION:
        return `<strong>${notification.userName}</strong> ${notification.action}`;
      case NotificationType.ASSIGNED:
        return `<strong>${notification.userName}</strong> ${notification.action}`;
      case NotificationType.STATUS_CHANGE:
        const statusMatch = notification.action.match(/changed status to (.+)/);
        const status = statusMatch ? statusMatch[1] : '';
        return `<strong>${notification.userName}</strong> changed status to <strong>${status}</strong>`;
      default:
        return `${notification.userName} ${notification.action}`;
    }
  }

  getTimeAgo(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  }

  close(): void {
    this.dialogRef.close();
  }

  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
    const currentFilter = this.filterSubject.value;
    this.onFilterChange({ ...currentFilter, type: this.TAB_TYPES[event.index] as any });
  }

  onToggleChange(showOnlyUnread: boolean): void {
    const currentFilter = this.filterSubject.value;
    this.onFilterChange({ ...currentFilter, showOnlyUnread });
  }
}